import { EventType } from "@/analytics/analytics-events";
import { StartEditWaypointsToolProperties } from "@/analytics/edit-waypoints-tool-properties";
import { MeasurementModeProperties } from "@/analytics/measurement-mode-events-properties";
import { useTransparencySettingsContext } from "@/components/common/transparency-sliders/transparency-settings-context";
import { usePhoneViewerLayout } from "@/hooks/use-phone-viewer-layout";
import { useCurrentArea, useCurrentScene } from "@/modes/mode-data-context";
import { useExportModePermissions } from "@/modes/overview-mode/use-can-use-export-mode";
import { selectIsPanoWithDepth } from "@/modes/walk-mode/walk-scene-selectors";
import {
  PanoMeasurementToolState,
  setActiveMeasurement,
  setPanoMeasurementToolState,
} from "@/store/measurement-tool-slice";
import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import {
  setActiveAnalysisId,
  setActiveAnalysisVisibility,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import {
  AnnotationIcon,
  BlurIcon,
  ChangeIndicatorBadge,
  EditWaypointsIcon,
  ExportIcon,
  FaroText,
  FaroButton,
  HeatmapIcon,
  OpacitySettingsIcon,
  PointCloudAnalysisIcon,
  RulerIcon,
  ScreenshotIcon,
  SheetSaturationToggleIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  isIElementGenericImgSheet,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { selectChildDepthFirst } from "@faro-lotv/project-source";
import { useCallback, type JSX, type ReactNode } from "react";
import { useSceneEvents } from "../common/scene-events-context";
import { ClipSceneIcon, ClipSceneSubmenu } from "./clip-scene-tool-ui";
import { PanoMeasurementToolPanel } from "./pano-measurement-tool-panel";
import { MobileViewerSheet } from "./mobile-viewer-sheet";
import { ToolGroup, ToolStoreField, ToolsToolbar } from "./tools-toolbar";
import {
  disableScaleAreaMessage,
  useDisableCaptureTreeAlignment,
} from "./tree/cad-model-tree/use-disable-capture-tree-alignment";

/**
 * @returns A toolbar providing buttons to activate tools to use within the canvas
 *  Visibility of the tools is controlled through the ui store slice
 */
export function CanvasToolbar(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const isPhone = usePhoneViewerLayout();

  const currentMode = useAppSelector(selectModeName);

  const activeTool = useAppSelector(selectActiveTool);

  const { main } = useCurrentScene();
  const isPanoObjectWithDepth = useAppSelector(selectIsPanoWithDepth(main));

  const onMeasurementToolClick = useCallback(() => {
    let context: MeasurementModeProperties["context"] = "3D";
    if (main && isIElementImg360(main)) {
      context = isPanoObjectWithDepth ? "pano" : "360";
    }

    Analytics.track<MeasurementModeProperties>(
      activeTool === ToolName.measurement
        ? EventType.disableMeasurementMode
        : EventType.enableMeasurementMode,
      { context },
    );
    if (activeTool === ToolName.measurement) {
      // User clicked on the measurement tool button to turn it off
      dispatch(setActiveMeasurement(undefined));
      dispatch(setPanoMeasurementToolState(PanoMeasurementToolState.None));
    }
  }, [activeTool, dispatch, isPanoObjectWithDepth, main]);

  const desaturation =
    useAppSelector(selectActiveTool) === ToolName.desaturateSheet;
  const onDesaturationToolClick = useCallback(() => {
    Analytics.track(
      desaturation
        ? EventType.turnDesaturationOff
        : EventType.turnDesaturationOn,
    );
  }, [desaturation]);

  const onEditWaypointsToolClick = useCallback(() => {
    Analytics.track<StartEditWaypointsToolProperties>(
      EventType.startEditWaypointsTool,
      { via: "toolbar" },
    );
  }, []);

  const isClipSceneActive =
    useAppSelector(selectActiveTool) === ToolName.clipScene;
  const onClipSceneToolClick = useCallback(() => {
    Analytics.track(
      isClipSceneActive ? EventType.hideClippingBox : EventType.showClippingBox,
    );
  }, [isClipSceneActive]);

  const { area } = useCurrentArea();

  const {
    requiredPermissionsMessage: requiredPermissionsForExportToolMessage,
  } = useExportModePermissions();

  const showMeasureInPanoToolPanel =
    activeTool === ToolName.measurement && currentMode === "walk";

  const { takeScreenshot } = useSceneEvents();

  const tools: ToolGroup[] = [
    {
      key: "tools",
      storeField: ToolStoreField.activeTool,
      tools: [
        {
          name: ToolName.measurement,
          icon: <RulerIcon />,
          tooltip: {
            default: "Measure",
            isInteractive: currentMode === "sheet",
            waiting: "Loading additional data for measurement.",
            disabled: <ToolTipForDisabledMeasurementTool />,
          },
          subMenu: showMeasureInPanoToolPanel ? (
            <PanoMeasurementToolPanel />
          ) : undefined,
          onClick: onMeasurementToolClick,
        },
        {
          name: ToolName.analysis,
          icon: <PointCloudAnalysisIcon />,
          onClick: () => {
            if (activeTool !== ToolName.analysis) {
              // hide the previously active analysis
              dispatch(
                setActiveAnalysisVisibility({
                  visible: false,
                }),
              );
              dispatch(setActiveAnalysisId(undefined));
            }
            Analytics.track(
              activeTool === ToolName.analysis
                ? EventType.closeColoredAnalysisTool
                : EventType.openColoredAnalysisTool,
            );
          },
          tooltip: {
            default: "Point cloud analysis",
            disabled: "A point cloud must be displayed to perform an analysis",
          },
        },
        {
          name: ToolName.annotation,
          icon: <AnnotationIcon />,
          tooltip: {
            default: "Create annotation",
            isInteractive: currentMode === "sheet",
            waiting: "Loading additional data for annotation.",
            disabled: <TooltipForDisabledAnnotation />,
          },
        },
        {
          name: ToolName.manualBlurring,
          icon: <BlurIcon />,
          onClick: () => Analytics.track(EventType.startManualBlurringTool),
          tooltip: {
            default: "Manual blurring",
            isInteractive: currentMode === "sheet",
            disabled: "Manual blurring is only available for Project Editors",
          },
        },
        {
          name: ToolName.export,
          icon: <ExportIcon />,
          tooltip: {
            default: "Export",
            disabled:
              requiredPermissionsForExportToolMessage ??
              "Export tool is not available. You can only use it with point cloud data.",
          },
          onClick: () => Analytics.track(EventType.startExportTool),
        },
        {
          name: ToolName.clipScene,
          icon: <ClipSceneIcon />,
          tooltip: {
            default: "Clip scene",
            disabled:
              "Clip scene tool is not available when 360° Photo is selected",
          },
          subMenu: (
            <ClipSceneSubmenu
              onReset={() => {
                Analytics.track(EventType.resetClippingBox);
              }}
              onAutoClipped={() => {
                Analytics.track(EventType.applyAutoClippingBox);
              }}
            />
          ),
          onClick: onClipSceneToolClick,
        },
        {
          name: ToolName.desaturateSheet,
          icon: <SheetSaturationToggleIcon />,
          tooltip: {
            default: "Toggle desaturation of sheet",
            disabled:
              "Toggling the desaturation state of the sheet is not available at the moment.",
          },
          onClick: onDesaturationToolClick,
        },
        {
          name: ToolName.editWaypoints,
          icon: <EditWaypointsIcon />,
          tooltip: {
            default: "Edit waypoints and location markers",
            isInteractive: currentMode === "sheet",
            disabled:
              "Waypoint management is only available for Project Editors",
          },
          onClick: onEditWaypointsToolClick,
        },
      ],
    },
    {
      key: "settings",
      storeField: ToolStoreField.activeSettingsMenu,
      tools: [
        {
          name: ToolName.opacity,
          icon: <OpacityIcon />,
          tooltip: {
            default: "Opacity",
          },
        },
        {
          name: ToolName.heatmap,
          icon: <HeatmapIcon />,
          tooltip: {
            default: "Heatmap comparison",
          },
        },
      ],
    },
    {
      key: "clickable actions",
      storeField: ToolStoreField.none,
      tools: [
        {
          name: ToolName.screenshot,
          icon: <ScreenshotIcon />,
          tooltip: {
            default: "Take screenshot",
            waiting: "Creating the screenshot",
          },
          onClick: () => takeScreenshot.emit(),
        },
      ],
    },
  ];

  // Do not show any tool if in sheet mode without a sheet
  const hasSheet = useAppSelector(
    selectChildDepthFirst(area, isIElementGenericImgSheet),
  );
  if (!hasSheet && currentMode === "sheet") {
    if (isPhone) {
      return (
        <MobileViewerSheet name="tools" title="Tools">
          <FaroText variant="bodyM">No tools available</FaroText>
        </MobileViewerSheet>
      );
    }
    return null;
  }

  return <ToolsToolbar toolGroups={tools} allowOverflow />;
}

/**
 * @returns Icon for the opacity toolbar that is showing a badge dot while any opacity is applied to the models
 */
function OpacityIcon(): JSX.Element {
  const { areDefaultOpacities, objectsOpacity } =
    useTransparencySettingsContext((s) => s);

  const isNotSplitMode = useAppSelector(selectModeName) !== "split";

  return (
    <ChangeIndicatorBadge
      invisible={areDefaultOpacities(objectsOpacity, isNotSplitMode)}
    >
      <OpacitySettingsIcon />
    </ChangeIndicatorBadge>
  );
}

/** @returns an appropriate disabled message tooltip for Annotation tool */
function TooltipForDisabledAnnotation(): ReactNode {
  const dispatch = useAppDispatch();

  const currentMode = useAppSelector(selectModeName);

  const { area } = useCurrentArea();
  const disableScaling = useDisableCaptureTreeAlignment(area);

  const { activeElement } = useCurrentScene();

  const hasWritePermission = useAppSelector(selectHasWritePermission);
  if (!hasWritePermission) {
    return <>Annotation creation is only available for Project Editors</>;
  }

  if (currentMode === "sheet") {
    if (disableScaling) {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <>{disableScaleAreaMessage}</>;
    }
    return (
      <>
        Annotations are possible only on floor images with a scale. Define the
        scale first.
        <FaroButton
          variant="ghost"
          size="s"
          dark
          onClick={() => dispatch(changeMode("floorscale"))}
        >
          Set scale
        </FaroButton>
      </>
    );
  } else if (isIElementImg2d(activeElement)) {
    return "Annotations are currently not supported in 2D images.";
  } else if (currentMode === "walk" && isIElementOrthomosaic(activeElement)) {
    return "Annotations are currently not supported on orthomosaic images.";
  }
  return "Annotations are currently not available.";
}

/**
 * @returns an appropriate disabled message tooltip for the Measurement tool
 */
function ToolTipForDisabledMeasurementTool(): ReactNode {
  const currentMode = useAppSelector(selectModeName);

  const { area } = useCurrentArea();
  const disableScaling = useDisableCaptureTreeAlignment(area);

  const dispatch = useAppDispatch();

  const { activeElement } = useCurrentScene();

  if (currentMode === "sheet") {
    return (
      <>
        Measurements are possible only on floor images with a scale.
        {disableScaling ? (
          <>
            <br />
            {disableScaleAreaMessage}
          </>
        ) : (
          <>
            <br />
            Define the scale first.
            <FaroButton
              variant="ghost"
              size="s"
              dark
              onClick={() => dispatch(changeMode("floorscale"))}
            >
              Set scale
            </FaroButton>
          </>
        )}
      </>
    );
  } else if (isIElementImg2d(activeElement)) {
    return "Measurements are currently not supported in 2D images.";
  } else if (currentMode === "walk" && isIElementOrthomosaic(activeElement)) {
    return "Measurements currently not supported on orthomosaic images.";
  }

  return "Measurement are currently not available.";
}
